import { Link } from '@suntex.core/nav';
import type {
  Database_Procs_GetGoalsIndexPlayers_Item,
  Database_Procs_GetGoalsIndexTeams_Item,
} from '@suntex.fim/api-client';
import { CellCheckBox, ColumnType, HeaderCheckBox } from '@suntex.fim/ui';

import { renderHeaderTooltip } from '../utils/reportLabels';

/** Minimal cell-context shape required by column cell renderers in this file */
type CellInfo = {
  getValue: () => unknown;
  row: { original: GoalIndexReportData };
};

export type GoalIndexReportData =
  | Database_Procs_GetGoalsIndexPlayers_Item
  | Database_Procs_GetGoalsIndexTeams_Item;

/**
 * Common hidable column labels shared between player and team tables.
 */
const commonHidableLabels = {
  composite: 'Goal Index (Out of 100)',
  activity: 'Activity (Out of 25)',
  fact: 'Fact Fluency (Out of 25)',
  wordProblem: 'Word Problem Fluency (Out of 25)',
  procedural: 'Procedural Fluency (Out of 25)',
};

/** Hidable column label map for player-level tables */
export const hidableSelectLabel: Record<string, string> = {
  playerName: 'Player Name',
  refName: 'Display Name',
  ...commonHidableLabels,
};

/** Hidable column label map for team-level tables */
export const teamHidableSelectLabel: Record<string, string> = {
  teamName: 'Team Name',
  className: 'Class Name',
  ...commonHidableLabels,
};

type SpecificColumnConfig = Record<
  string,
  { header: string; id: string; customCell?: (info: CellInfo) => JSX.Element }
>;

const generateColumns = (
  specificColumns: SpecificColumnConfig,
): ColumnType<GoalIndexReportData>[] => {
  const commonColumns = Object.entries(commonHidableLabels).map(
    ([key, label]) => ({
      header: () => renderHeaderTooltip(label),
      id: key,
      accessorKey: key,
      cell: (info: CellInfo) =>
        info.getValue() ? <b>{info.getValue() as string}</b> : <b>0</b>,
      size: 100,
    }),
  );

  const specificColumnsMapped = Object.entries(specificColumns).map(
    ([key, { header, id, customCell }]) => ({
      header: () => renderHeaderTooltip(header),
      id,
      accessorKey: key,
      cell:
        customCell ??
        ((info: CellInfo) =>
          info.getValue() ? <b>{info.getValue() as string}</b> : <b>None</b>),
      size: 100,
    }),
  );

  return [...specificColumnsMapped, ...commonColumns];
};

/** Column definitions for the player-level Goals Index by Player table */
export const columns = (): ColumnType<GoalIndexReportData>[] => [
  {
    id: 'select',
    size: 40,
    enableResizing: false,
    enableSorting: false,
    header: HeaderCheckBox,
    cell: CellCheckBox,
  },
  ...generateColumns({
    playerName: {
      header: 'Player Name',
      id: 'playerName',
      customCell: (info: CellInfo) => {
        const value = info.getValue() as string;
        return <b>{value ? value : 'None'}</b>;
      },
    },
    refName: { header: 'Display Name', id: 'refName' },
  }),
];

/** Column definitions for the school-level Goals Index by Team table */
export const teamColumns = (): ColumnType<GoalIndexReportData>[] =>
  generateColumns({
    teamName: {
      header: 'Team Name',
      id: 'teamName',
      customCell: (info: CellInfo) => {
        const value = info.getValue() as string;
        const teamId = (info.row.original as unknown as { teamId: string })
          .teamId;
        return (
          <b>
            {value ? (
              <Link to={`/team/${teamId}/assessment-report/goals-index`}>
                {value}
              </Link>
            ) : (
              'None'
            )}
          </b>
        );
      },
    },
    className: {
      header: 'Class Name',
      id: 'className',
      customCell: (info: CellInfo) => {
        const val = info.getValue();
        return typeof val === 'string' && val.length > 0 ? (
          <b>{val}</b>
        ) : (
          <b>None</b>
        );
      },
    },
  });
